import React, { useRef, useState } from "react";
import { InternalLayout } from "@/components/InternalLayout";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Loader2, ArrowRight, RotateCcw, FileSpreadsheet, Sparkles } from "lucide-react";
import api from "@/lib/api";
import useDiscovery from "./useDiscovery";
import Akte from "./Akte";
import QuestionCard from "./QuestionCard";
import ReadinessView from "./ReadinessView";
import SummaryView from "./SummaryView";

// Discovery module screen (AP-02). Flow: Kickoff -> adaptive interview
// (live "Projektakte" fills itself in) -> readiness -> generated artifacts.
// State lives in Supabase; this UI is a client of /api/os/discovery/*.

const STEPS = [
  ["kickoff", "Kickoff"],
  ["interview", "Interview"],
  ["readiness", "Readiness"],
  ["summary", "Ergebnis"],
];

export default function DiscoveryLab() {
  const d = useDiscovery();
  const stepIndex = STEPS.findIndex(([k]) => k === d.phase);

  return (
    <InternalLayout>
      <style>{`
        .dlab-flash { animation: dlabFlash 1.6s ease-out 1; }
        @keyframes dlabFlash {
          0% { background-color: rgba(57,190,177,0.28); }
          100% { background-color: transparent; }
        }
        @media (prefers-reduced-motion: reduce) {
          .dlab-flash { animation: none; }
        }
      `}</style>

      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap" data-testid="dlab-page">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">AppWerkstatt OS · Modul</div>
          <h1 className="font-display text-3xl md:text-4xl text-foreground">Discovery</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Aus einer App-Idee wird ein entscheidungsfähiger, baubarer Projektkontext — die KI fragt zurück, strukturiert und leitet ab, was sich sicher ableiten lässt.
          </p>
        </div>
        {d.sessionId ? (
          <div className="flex items-center gap-3">
            <span className="font-mono text-[10px] text-muted-foreground">{d.sessionId}</span>
            <button
              type="button"
              onClick={d.reset}
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
              data-testid="dlab-reset"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Neue Session
            </button>
          </div>
        ) : null}
      </div>

      {/* Stepper — a real sequence: numbers carry order */}
      <div className="flex items-center gap-0 mb-8 border border-border bg-card">
        {STEPS.map(([key, label], i) => {
          const isActive = i === stepIndex;
          const isDone = i < stepIndex;
          return (
            <div
              key={key}
              className={
                "flex-1 px-4 py-2.5 flex items-center gap-2 border-b-2 " +
                (isActive ? "border-b-aw-coral" : "border-b-transparent")
              }
            >
              <span
                className={
                  "font-mono text-[10px] " +
                  (isDone ? "text-aw-turquoise" : isActive ? "text-aw-coral" : "text-muted-foreground/50")
                }
              >
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className={"text-xs " + (isActive ? "text-foreground font-medium" : isDone ? "text-foreground/70" : "text-muted-foreground/50")}>
                {label}
              </span>
            </div>
          );
        })}
      </div>

      {d.error ? (
        <div className="mb-6 border border-red-600/30 bg-red-600/5 text-sm text-red-800 px-4 py-3 flex items-start justify-between gap-4" data-testid="dlab-error">
          <span>{d.error}</span>
          <button type="button" onClick={() => d.setError(null)} className="text-xs underline shrink-0">Schließen</button>
        </div>
      ) : null}

      {d.phase === "kickoff" && <Kickoff d={d} />}

      {d.phase === "interview" && (
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-7">
            <QuestionCard state={d.state} questionId={d.currentQuestionId} busy={d.busy} onSubmit={d.answer} />
          </div>
          <div className="col-span-12 lg:col-span-5">
            <Akte state={d.state} freshInferred={d.freshInferred} thinking={d.busy === "answer"} />
          </div>
        </div>
      )}

      {d.phase === "readiness" && (
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-7">
            <ReadinessView state={d.state} busy={d.busy} onGenerate={d.generateArtifacts} />
          </div>
          <div className="col-span-12 lg:col-span-5">
            <Akte state={d.state} />
          </div>
        </div>
      )}

      {d.phase === "summary" && <SummaryView state={d.state} busy={d.busy} onRegenerate={d.generateArtifacts} />}
    </InternalLayout>
  );
}

function Kickoff({ d }) {
  const [problem, setProblem] = useState("");
  const [projectName, setProjectName] = useState("");
  const [clientName, setClientName] = useState("");
  const [resumeId, setResumeId] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analyzeError, setAnalyzeError] = useState(null);
  const fileRef = useRef(null);
  const canStart = problem.trim().length >= 10 && projectName.trim().length >= 2;

  async function analyzeFile(file) {
    if (!file) return;
    setAnalyzing(true);
    setAnalyzeError(null);
    setAnalysis(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/os/discovery/analyze-file", fd, { timeout: 120000 });
      setAnalysis(r.data);
      const kickoff = r.data?.analysis?.kickoff_text;
      if (kickoff) setProblem(kickoff);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setAnalyzeError(typeof detail === "string" ? detail : "Die Datei konnte nicht analysiert werden.");
    } finally {
      setAnalyzing(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  return (
    <div className="grid grid-cols-12 gap-6">
      <div className="col-span-12 lg:col-span-8 space-y-6">
        {/* File analysis (optional pre-step): the system reads the customer's
            spreadsheet and drafts the idea text below. */}
        <div className="bg-card border border-border" data-testid="dlab-analyze">
          <div className="border-b border-border px-6 py-3 flex items-center justify-between">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Datei-Analyse · optional</div>
            {analysis?.filename ? <span className="font-mono text-[10px] text-muted-foreground">{analysis.filename}</span> : null}
          </div>
          <div className="px-6 py-5 space-y-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <p className="text-sm text-muted-foreground max-w-md">
                Sie haben bereits eine Excel- oder CSV-Datei, in der heute alles gepflegt wird? Laden Sie sie hoch — das System liest sie und formuliert den Vorhabens-Text vor.
              </p>
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xlsm,.csv"
                className="hidden"
                onChange={(e) => analyzeFile(e.target.files?.[0])}
                data-testid="dlab-file-input"
              />
              <button
                type="button"
                disabled={analyzing}
                onClick={() => fileRef.current?.click()}
                className="inline-flex items-center gap-2 border border-border text-foreground text-sm px-4 py-2 rounded-full disabled:opacity-40 hover:bg-secondary transition shrink-0"
                data-testid="dlab-file-button"
              >
                {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
                {analyzing ? "Das System liest Ihre Datei …" : "Excel/CSV hochladen"}
              </button>
            </div>

            {analyzeError ? (
              <div className="border border-red-600/30 bg-red-600/5 text-sm text-red-800 px-4 py-3" data-testid="dlab-analyze-error">
                {analyzeError}
              </div>
            ) : null}

            {analysis ? (
              <div className="border border-aw-turquoise/40 bg-aw-turquoise/5 px-4 py-4 space-y-3 dlab-flash" data-testid="dlab-analyze-panel">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-aw-turquoise" />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-aw-turquoise">Das sehe ich in Ihrer Datei</span>
                </div>
                <p className="text-sm text-foreground">{analysis.analysis?.beobachtung}</p>
                {analysis.analysis?.app_vorschlag ? (
                  <p className="text-sm text-muted-foreground"><span className="font-medium text-foreground">Vorschlag:</span> {analysis.analysis.app_vorschlag}</p>
                ) : null}
                {analysis.sheets?.length ? (
                  <p className="font-mono text-[10px] text-muted-foreground">
                    {analysis.sheets.map((s) => `${s.name} (${s.rows_total} Zeilen)`).join(" · ")}
                  </p>
                ) : null}
                {analysis.analysis?.rueckfragen?.length ? (
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Meine Nachfragen</div>
                    <ul className="space-y-1">
                      {analysis.analysis.rueckfragen.map((q, i) => (
                        <li key={i} className="text-sm text-foreground flex gap-2">
                          <span className="text-aw-coral font-mono text-xs shrink-0 mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                          {q}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {analysis.analysis?.kickoff_text ? (
                  <p className="text-xs text-muted-foreground">Der Vorhabens-Text unten wurde aus der Datei vorformuliert — bitte prüfen und ergänzen.</p>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canStart) d.start({ problem: problem.trim(), projectName: projectName.trim(), clientName: clientName.trim() });
          }}
          className="bg-card border border-border"
          data-testid="dlab-kickoff"
        >
          <div className="border-b border-border px-6 py-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Neue Discovery</div>
          </div>
          <div className="px-6 py-6 space-y-4">
            <h2 className="font-display-medium text-xl md:text-2xl text-foreground">Was möchten Sie verbessern?</h2>
            <p className="text-sm text-muted-foreground max-w-2xl">
              Beschreiben Sie Ihr Vorhaben in Ihren eigenen Worten: Worum geht es, wer arbeitet damit, was ist heute umständlich? Je konkreter die Beschreibung, desto mehr kann die KI bereits ableiten.
            </p>
            <Textarea
              value={problem}
              onChange={(e) => setProblem(e.target.value)}
              placeholder="Beispiel: Wir betreiben einen Betriebshof mit Werkstatt und wollen unser Material- und Gerätelager digital in den Griff bekommen …"
              rows={7}
              className="bg-background text-base"
              data-testid="dlab-problem-input"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground block mb-1.5" htmlFor="dlab-project">Projektname</label>
                <Input id="dlab-project" value={projectName} onChange={(e) => setProjectName(e.target.value)} placeholder="z. B. Platzwart" className="bg-background" data-testid="dlab-project-input" />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground block mb-1.5" htmlFor="dlab-client">Kunde (optional)</label>
                <Input id="dlab-client" value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Organisation" className="bg-background" />
              </div>
            </div>
          </div>
          <div className="border-t border-border px-6 py-4 flex justify-end">
            <button
              type="submit"
              disabled={!canStart || d.busy === "intake"}
              className="inline-flex items-center gap-2 bg-aw-coral text-white font-medium text-sm px-5 py-2.5 rounded-full disabled:opacity-40 hover:brightness-95 transition"
              data-testid="dlab-start"
            >
              {d.busy === "intake" ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {d.busy === "intake" ? "Die KI klassifiziert Ihr Vorhaben …" : "Discovery starten"}
              {d.busy !== "intake" ? <ArrowRight className="w-4 h-4" /> : null}
            </button>
          </div>
        </form>
      </div>

      <div className="col-span-12 lg:col-span-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (resumeId.trim()) d.resume(resumeId);
          }}
          className="bg-card border border-border"
          data-testid="dlab-resume"
        >
          <div className="border-b border-border px-5 py-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Session fortsetzen</div>
          </div>
          <div className="px-5 py-5 space-y-3">
            <p className="text-xs text-muted-foreground">Bestehende Discovery per Session-ID öffnen — inklusive Akte, Readiness und Artefakten.</p>
            <Input
              value={resumeId}
              onChange={(e) => setResumeId(e.target.value)}
              placeholder="Session-ID"
              className="bg-background font-mono text-xs"
              data-testid="dlab-resume-input"
            />
            <button
              type="submit"
              disabled={!resumeId.trim() || d.busy === "resume"}
              className="inline-flex items-center gap-2 border border-border text-foreground text-sm px-4 py-2 rounded-full disabled:opacity-40 hover:bg-secondary transition"
              data-testid="dlab-resume-submit"
            >
              {d.busy === "resume" ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Laden
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
